import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GridAuditCellsService } from './grid-audit-cells.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-grid-audit-cells',
    templateUrl: './grid-audit-cells.component.html',
    styleUrls: ['./grid-audit-cells.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GridAuditCellsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  loading = false;
  minimise = false;
  modalTitle = 'Audit Screen';
  fromDate = '';
  toDate = '';
  searchText = '';
  undoData: any[] = [];
  filteredUndoData: any[] = [];

  private isInitialized = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: GridAuditCellsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) {
      return;
    }
    this.isInitialized = true;
    this.modalTitle = `${this.rowData?.label || this.rowData?.COL_LABEL || 'Cell'} Audit Screen`;
    this.getCellAuditData();
  }

  private getRowKey(): any {
    return this.rowData?.row_id || this.rowData?.ROW_KEY || this.rowData?.rowKey || this.filterParams?.row_key;
  }

  private getColKey(): any {
    return this.rowData?.col_id || this.rowData?.COL_KEY || this.rowData?.colKey || this.filterParams?.col_key;
  }

  private getScreenKey(): any {
    return this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || this.filterParams?.screen_key;
  }

  getCellAuditData(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) {
      return;
    }
    this.loading = true;
    this.cdr.detectChanges();
    const payload = {
      filters: JSON.stringify(this.filterParams || {}),
      screen_key: this.getScreenKey(),
      row_key: this.getRowKey(),
      col_key: this.getColKey()
    };

    this.service.getCellAuditByContext(baseUrl, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.callSuccess === '1') {
          this.undoData = Array.isArray(data?.CellAuditData) ? data.CellAuditData : [];
        } else {
          this.undoData = [];
        }
        this.applyFilter();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.undoData = [];
        this.filteredUndoData = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  applyFilter(): void {
    const from = this.fromDate ? new Date(this.fromDate) : null;
    const to = this.toDate ? new Date(this.toDate) : null;
    const search = (this.searchText || '').toLowerCase();
    this.filteredUndoData = (this.undoData || []).filter((item: any) => {
      const updated = new Date(item?.updated_on || item?.created_on || '');
      const datePass = (!from || updated >= from) && (!to || updated <= to);
      if (!datePass) {
        return false;
      }
      if (!search) {
        return true;
      }
      const text = JSON.stringify(item || {}).toLowerCase();
      return text.includes(search);
    });
  }

  clearSearch(): void {
    this.searchText = '';
    this.applyFilter();
    this.cdr.detectChanges();
  }

  revert(detail: any): void {
    // AngularJS used GridSaveFactory.saveSwitch; CE host listens for refresh and updates grid.
    this.requestGridUpdate.emit('refresh');
  }

  getTimeAgo(value: any): string {
    if (!value) {
      return '';
    }
    const dt = new Date(value);
    const seconds = Math.floor((Date.now() - dt.getTime()) / 1000);
    if (Number.isNaN(seconds)) {
      return '';
    }
    if (seconds < 60) return 'just now';
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
